export interface ShopProfile {
  id: string;
  email: string;
  phone?: string | null;
  name: string;
  status: string;
  agentId?: string;
  costRate?: number;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  emailVerifiedAt?: string | null;
  phoneVerifiedAt?: string | null;
  /** Effective hierarchy + shop flag; from login when present */
  can_delegate_popups?: boolean;
}

export type ShopLoginSuccessData = {
  accessToken: string;
  refreshToken: string;
  shop: Record<string, unknown>;
};

export type ShopLoginOtpChallengeData = {
  needsOtp: true;
  pendingToken: string;
  channel: 'email' | 'phone';
  maskedDestination?: string;
};

export interface ShopLoginResponse {
  success: boolean;
  data?: ShopLoginSuccessData | ShopLoginOtpChallengeData;
  message?: string;
}

export function isLoginOtpChallenge(
  data: ShopLoginResponse['data']
): data is ShopLoginOtpChallengeData {
  return !!data && 'needsOtp' in data && data.needsOtp === true;
}

export interface ShopDashboardData {
  credit_balance: number;
  shop_funding_debt?: {
    outstanding_credits: number;
    frozen: boolean;
    online_deposits_blocked: boolean;
  };
  online_balance_net?: number;
  player_count: number;
  /** Shop cost rate 0–1 (from agent); pricing = pay × rate per credit. */
  cost_rate?: number;
  /** Effective: SA delegation AND agent send AND shop player popup flag */
  can_delegate_popups?: boolean;
  can_create_player_popup?: boolean;
  low_balance_warning_enabled?: boolean;
  low_balance_threshold_usd?: number;
  low_balance_effective_threshold_usd?: number;
  two_factor_enabled?: boolean;
  two_factor_channel?: 'email' | 'phone' | null;
  email_verified?: boolean;
  phone_verified?: boolean;
}

export interface ShopHomeProblemCount {
  count: number;
  link: string;
}

export interface ShopHomeLowBalance {
  active: boolean;
  creditBalance: number;
  usdBalanceNet: number;
  thresholdUsd: number;
  link: string;
}

export interface ShopHomeTodayFlow {
  moneyIn: number;
  moneyOut: number;
  linkIn: string;
  linkOut: string;
}

export interface ShopHomeData {
  problems: {
    pendingWithdrawals: ShopHomeProblemCount;
    failedGameWallets: ShopHomeProblemCount;
    cashierExceptions: ShopHomeProblemCount;
    lowBalance: ShopHomeLowBalance;
  };
  badgeCount: number;
  today?: ShopHomeTodayFlow;
  cashierPortalUrl: string | null;
}

export interface ShopHomeSearchPlayer {
  id: string;
  username: string | null;
  phone: string | null;
  email: string | null;
  status: string | null;
  balance: number;
  link: string;
}

export interface ShopHomeSearchTransaction {
  kind: 'credit' | 'usd';
  id: string;
  transactionId?: string;
  type: string | null;
  amount: number;
  createdAt: string | null;
  link: string;
}

export interface ShopHomeSearchData {
  q: string;
  players: ShopHomeSearchPlayer[];
  creditTransactions: ShopHomeSearchTransaction[];
  usdTransactions: ShopHomeSearchTransaction[];
}

/** POST /shop/credits/purchase — mirrors agent purchase payload shape. */
export interface ShopCreditPurchaseData {
  paymentUrl?: string | null;
  paymentAddress?: string | null;
  invoiceId?: string | null;
  transactionId?: string;
  amount: number;
  currency: string | null;
  creditAmount: number;
  endTime?: string | number | null;
  status: string;
  order?: Record<string, unknown>;
}

export interface ZeroxStaticWallet {
  id: string;
  ownerType: 'super_agent' | 'shop' | 'agent';
  ownerId: string;
  clientId: string;
  currency: string;
  address: string;
  tag?: string | null;
  enabled: boolean;
  raw?: unknown;
  created_at?: string;
  updated_at?: string;
}

/** Player row from GET /shop/players (Sequelize JSON, underscored keys) */
export interface ShopPlayer {
  id: string;
  username: string | null;
  email: string | null;
  phone: string | null;
  phone_verified?: boolean;
  phone_verified_at?: string | null;
  email_verified?: boolean;
  email_verified_at?: string | null;
  status: string;
  banned?: boolean;
  ban_reason?: string | null;
  banned_at?: string | null;
  shop_banned?: boolean;
  shop_ban_reason?: string | null;
  shop_ban_period?: '1d' | '3d' | '7d' | '30d' | 'permanent' | null;
  shop_ban_until?: string | null;
  shop_banned_at?: string | null;
  shop_ban_active?: boolean;
  admin_banned?: boolean;
  admin_ban_reason?: string | null;
  admin_ban_period?: '1d' | '3d' | '7d' | '30d' | 'permanent' | null;
  admin_ban_until?: string | null;
  admin_banned_at?: string | null;
  admin_ban_active?: boolean;
  balance?: number;
  shop_assigned_at: string | null;
  last_login_at: string | null;
  terminate_reason?: string | null;
  terminated_at?: string | null;
  terminated_by?: string | null;
}

export interface ShopPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** wallet_transactions.type values (and legacy string fallbacks). */
export type ShopWalletTxType =
  | 'deposit'
  | 'withdrawal'
  | 'transfer'
  | 'bonus'
  | 'adjustment'
  | 'reservation'
  | 'reservation_release'
  | 'credit'
  | 'debit'
  | string;

/** Enriched by GET /shop/transactions for shop UI */
export interface ShopLedgerPresentation {
  title: string;
  direction: 'in' | 'out' | 'neutral';
  signedAmount: number;
  counterparty: string | null;
  shortRef: string | null;
  fullId: string;
}

/** Row from GET /shop/transactions (wallet_transactions + presentation). */
export interface ShopWalletLedgerRow {
  id: string;
  idempotencyKey?: string;
  ownerType?: string;
  ownerId?: string;
  type: ShopWalletTxType;
  amount: string | number;
  balanceBefore?: string | number;
  balanceAfter?: string | number;
  /** Some responses use snake_case */
  balance_before?: string | number;
  balance_after?: string | number;
  /** Snapshot of from_wallet balance on wallet_transactions (payer), not player wallet */
  payerBalanceBefore?: string | number | null;
  payerBalanceAfter?: string | number | null;
  payer_balance_before?: string | number | null;
  payer_balance_after?: string | number | null;
  sourceChannel?: string | null;
  payerWallet?: { ownerType?: string; ownerId?: string } | null;
  recipientWallet?: { ownerType?: string; ownerId?: string } | null;
  referenceId?: string | null;
  referenceType?: string | null;
  businessTransactionId?: string | null;
  /** May appear at root or inside metadata (wallet tx ledger). */
  platformFeeRate?: string | number | null;
  platform_fee_rate?: string | number | null;
  platformFeeAmount?: string | number | null;
  platform_fee_amount?: string | number | null;
  /** Ledger snapshot: credits × shop/agent cost rate (may be at root or in metadata). */
  costRate?: string | number | null;
  cost_rate?: string | number | null;
  costAmount?: string | number | null;
  cost_amount?: string | number | null;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  createdBy?: string | null;
  created_at?: string;
  createdAt?: string;
  presentation?: ShopLedgerPresentation;
}

// ===== Online balance (USD wallet) ledger =====

export type ShopUsdWalletTxType =
  | 'deposit'
  | 'withdrawal'
  | 'purchase_credits'
  | 'adjustment'
  | 'reversal'
  | string;

export interface ShopUsdWalletLedgerParty {
  ownerType?: string;
  ownerId?: string | null;
  displayName?: string | null;
  roleLabel?: string;
  label?: string;
}

export interface ShopUsdWalletLedgerRow {
  id: string;
  usdWalletId: string;
  transactionId: string;
  type: ShopUsdWalletTxType;
  provider?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  amountGross: string | number;
  amountNet: string | number;
  balanceGrossBefore: string | number;
  balanceGrossAfter: string | number;
  balanceNetBefore: string | number;
  balanceNetAfter: string | number;
  metadata?: Record<string, unknown> | null;
  status?: string;
  createdAt?: string;
  created_at?: string;
  /** Enriched by backend ledger presenter */
  usdWallet?: {
    id?: string;
    ownerType?: string;
    ownerId?: string;
    currency?: string;
  } | null;
  fromParty?: ShopUsdWalletLedgerParty | null;
  toParty?: ShopUsdWalletLedgerParty | null;
  ledgerType?: string | null;
  paymentMethod?: string | null;
  displayLabel?: string | null;
  displayCategory?: string | null;
}

export interface ShopUsdWalletLedgerResponse {
  ownerType: 'shop';
  ownerId: string;
  currency: string;
  walletId: string | null;
  balanceGross: string | number;
  balanceNet: string | number;
  status: string;
  count: number;
  rows: ShopUsdWalletLedgerRow[];
}

/** Row from domains table (GET/POST/PATCH /shop/kiosk-domain) */
export interface ShopKioskDomainRecord {
  id: string;
  domain: string;
  type: string;
  ipWhitelist: string[] | null;
  isActive: boolean;
  label: string | null;
  shopId: string | null;
  isPrimary?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ShopKioskDomainPayload {
  domain: ShopKioskDomainRecord | null;
  domains?: ShopKioskDomainRecord[];
  hostnames?: string[];
  subdomain?: string | null;
  kiosk_base_domain: string;
  kiosk_base_domains?: string[];
}

// ===== Withdrawal approvals (shop) =====

export type ShopWithdrawalStatus =
  | 'pending'
  | 'processing'
  | 'awaiting_admin'
  | 'approved'
  | 'retry_scheduled'
  | 'completed'
  | 'rejected'
  | 'failed'
  | 'cancelled'
  | 'expired';

export interface ShopWithdrawalUser {
  id: string;
  email?: string;
  phone?: string | null;
  profile?: {
    firstName?: string;
    lastName?: string;
  };
  shop_id?: string | null;
  shopId?: string | null;
}

export interface ShopWithdrawalRequest {
  transactionId: string;
  userId: string;
  user: ShopWithdrawalUser;
  withdrawalType: string | null;
  amount: number;
  currency: string;
  description?: string | null;
  status: ShopWithdrawalStatus;
  createdAt?: string;
  updatedAt?: string;
  provider?: string | null;
  autoApproveSkipRule?: string | null;
  autoApproveSkipReason?: string | null;
  flaggedInfo?: {
    isFlagged?: boolean;
    triggeringRule?: { code: string; description: string } | null;
    reasons?: Array<{ flagType: string; description: string }>;
  } | null;
}

export interface ShopPendingWithdrawalsResponse {
  success: boolean;
  message?: string;
  data?: {
    withdrawals: ShopWithdrawalRequest[];
    threshold?: number;
    count?: number;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    sortWarning?: string;
  };
}

export interface ShopApproveWithdrawalResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface ShopRejectWithdrawalResponse {
  success: boolean;
  message?: string;
  data?: any;
}

// ===== Cashier management (shop) =====

export type ShopCashierStatus = 'active' | 'suspended' | 'terminated' | string;

export interface ShopCashierCurrentShift {
  id: string;
  openedAt: string;
  openingCashBalance: string | number;
  status?: string;
  scheduledStartAt?: string | null;
  scheduledEndAt?: string | null;
}

export type CashierLoginMethod = 'username' | 'email' | 'phone' | 'any';

export interface ShopCashier {
  id: string;
  name: string;
  username: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  loginMethod: CashierLoginMethod;
  status: ShopCashierStatus;
  currentShift: ShopCashierCurrentShift | null;
  totalTransactionsToday: number;
  lastLoginAt: string | null;
}

export interface CreateShopCashierPayload {
  username: string;
  password: string;
  fullName: string;
  email: string;
  phone: string;
  loginMethod?: CashierLoginMethod;
}

export type CashierShiftStatus =
  | 'draft'
  | 'scheduled'
  | 'cancelled'
  | 'no_show'
  | 'active'
  | 'suspended'
  | 'auto_locked'
  | 'pending_reconciliation'
  | 'pending_manager_review'
  | 'completed'
  | 'completed_with_discrepancy'
  | string;

export interface CashierShift {
  id: string;
  shopId: string;
  cashierId: string;
  deviceId?: string | null;
  shiftDate?: string | null;
  scheduledStartAt?: string | null;
  scheduledEndAt?: string | null;
  lateGraceMinutes?: number;
  note?: string | null;
  status: CashierShiftStatus;
  openedAt?: string;
  closedAt?: string | null;
  openingCashBalance?: string | number;
  closingCashCounted?: string | number | null;
  expectedClosingCash?: string | number | null;
  discrepancyAmount?: string | number | null;
  discrepancyFlagged?: boolean;
  actualStartAt?: string | null;
  actualEndAt?: string | null;
  noShowDetectedAt?: string | null;
  /** Set when the cashier pressed Start shift (before the device was ready). */
  checkedInAt?: string | null;
  closedReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * `deviceId` on shift writes: omitted = keep the current device (create: none),
 * `null` = any approved device (chosen at start), id = pin to that device.
 */
export interface ReassignShopShiftPayload {
  cashierId: string;
  deviceId?: string | null;
  reason?: string;
}

export interface CancelShopShiftPayload {
  reason: string;
}

export interface CreateReplacementShiftPayload {
  cashierId: string;
  deviceId?: string | null;
  reason: string;
}

export interface MoveShiftDevicePayload {
  deviceId: string;
  /** 3–500 characters. */
  reason: string;
}

export interface ReplacementShiftResult {
  originalShift: CashierShift;
  replacementShift: CashierShift;
}

export interface CreateShopShiftPayload {
  shiftDate: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  cashierId: string;
  /** `null` or omitted = any approved device, chosen when the cashier starts. */
  deviceId?: string | null;
  lateGraceMinutes?: number;
  note?: string;
}

export interface UpdateShopCashierPayload {
  fullName?: string;
  email?: string;
  phone?: string;
  loginMethod?: CashierLoginMethod;
  note?: string;
}

export type CashierDeviceStatus =
  | 'pending_approval'
  | 'active'
  | 'temporarily_blocked'
  | 'compromised'
  | 'lost'
  | 'retired'
  | string;

export interface CashierRef {
  id: string;
  username: string;
  fullName: string | null;
}

export type CashierDeviceApprovalMethod = 'shop_portal' | 'pairing_code';

/** Open request: a cashier pressed Start shift on this pending device. */
export interface CashierDeviceApprovalRequest {
  requestedAt: string;
  cashier: CashierRef | null;
  shift: {
    id: string;
    status: CashierShiftStatus;
    scheduledStartAt: string | null;
    scheduledEndAt: string | null;
  };
}

export interface CashierDeviceActiveShift {
  id: string;
  cashierId: string;
}

/** `ShopDeviceView` from `GET /shop/cashier-devices` (the fingerprint is never returned). */
export interface CashierDevice {
  id: string;
  shopId?: string;
  deviceName: string;
  status: CashierDeviceStatus;
  /** Last 4 hex characters of the id, upper-case. Shown on the counter screen too. */
  shortCode: string;
  deviceType?: string | null;
  operatingSystem?: string | null;
  browserVersion?: string | null;
  lastIp?: string | null;
  firstRegisteredAt?: string | null;
  lastActiveAt?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  /** `null` = approved before approval methods were recorded. */
  approvalMethod?: CashierDeviceApprovalMethod | null;
  lastCashier?: CashierRef | null;
  approvalRequest?: CashierDeviceApprovalRequest | null;
  activeShift?: CashierDeviceActiveShift | null;
  createdAt?: string;
  updatedAt?: string;
}

export type ShopDeviceView = CashierDevice;

export interface CashierDevicePairingCode {
  /** Shown as `XXXX-XXXX`. */
  code: string;
  expiresAt: string;
  name: string | null;
}

/** `GET /shop/fraud-indicators` → `wrongDeviceAttempts[]` (refused money operations). */
export interface WrongDeviceAttempt {
  cashierId: string;
  shiftId: string | null;
  attemptedAction: string;
  deviceId: string | null;
  occurredAt: string;
}

export type CashierCashDropStatus =
  | 'draft'
  | 'pending_handover'
  | 'pending_receiver_confirmation'
  | 'confirmed'
  | 'rejected'
  | 'cancelled'
  | 'disputed'
  | string;

export interface CashierCashDrop {
  id: string;
  shopId: string;
  cashierId: string;
  shiftId: string;
  deviceId?: string | null;
  totalAmount: string | number;
  status: CashierCashDropStatus;
  confirmedBy?: string | null;
  confirmedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type RedemptionApprovalStatus = 'pending' | 'approved' | 'rejected' | string;

export interface CashierRedemptionApproval {
  id: string;
  shopId: string;
  cashierTransactionId: string;
  amount: string | number;
  status: RedemptionApprovalStatus;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  note?: string | null;
  createdAt?: string;
  playerId?: string | null;
  cashierId?: string | null;
  /** How the Player approved this redeem before it reached the manager (null for older requests). */
  redeemConsent?: { id: string; via: 'code' | 'app' } | null;
}

export interface CashierShiftAdjustment {
  id: string;
  shiftId: string;
  amount: string | number;
  reason: string;
  approvedBy: string;
  createdAt?: string;
}

export interface CashierShiftTransaction {
  id: string;
  shopId: string;
  cashierId: string;
  shiftId: string;
  playerId: string;
  type: 'deposit_credits' | 'redeem_credits' | 'process_withdrawal' | string;
  amount: string | number;
  paymentMethod?: string | null;
  transactionStatus?: string;
  createdAt?: string;
}

export interface CashierShiftReportSummary {
  totalTransactions: number;
  totalDeposits: number;
  totalRedemptions: number;
  totalCashIn: number;
  totalCashOut: number;
  totalCashDrops: number;
  approvedAdjustments: number;
}

export interface CashierShiftReportReconciliation {
  openingCash: string | number | null;
  expectedClosingCash: number;
  actualClosingCash: string | number | null;
  discrepancy: string | number | null;
}

export interface CashierShiftReport {
  shift: CashierShift;
  summary: CashierShiftReportSummary;
  reconciliation: CashierShiftReportReconciliation;
  transactions: CashierShiftTransaction[];
  cashDrops: CashierCashDrop[];
  adjustments: CashierShiftAdjustment[];
}

// ===== Shop Game Wallets =====

export interface ShopGameProvider {
  id: string;
  name: string;
  code: string;
  totalWallets: number;
  inSync: number;
  pending: number;
  failed: number;
  neverSynced: number;
  lastSyncAt: string | null;
}

export interface ShopGameProvidersResponse {
  items: ShopGameProvider[];
  summary: {
    totalProviders: number;
    totalWallets: number;
    inSync: number;
    pending: number;
    failed: number;
  };
}

export interface ShopGameWallet {
  id: string;
  playerId: string;
  playerName: string;
  playerUsername: string;
  gameWalletId: string;
  providerId: string;
  providerName?: string | null;
  providerCode?: string | null;
  masterCreditBalance: number;
  gameWalletBalance: number;
  difference: number;
  syncStatus: 'in_sync' | 'pending' | 'failed' | 'never_synced';
  lastSyncAt: string | null;
  lastError: string | null;
}

export interface ShopGameWalletsResponse {
  items: ShopGameWallet[];
  total: number;
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: string;
  sortWarning?: string;
}

export interface ShopGameWalletSyncIssue {
  id: string;
  playerId: string;
  playerName: string;
  gameWalletId: string;
  providerId?: string | null;
  issueType: string;
  errorMessage: string;
  status: string;
}

export interface ShopGameWalletSyncIssuesResponse {
  items: ShopGameWalletSyncIssue[];
  total: number;
}

export interface ShopGameWalletTransaction {
  id: string;
  playerId: string;
  playerName: string;
  type: 'game_wallet_deposit' | 'game_wallet_recall' | 'game_wallet_sync';
  amount: number;
  status: string;
  reference: string;
  note: string;
  createdAt: string;
}

export interface ShopGameWalletTransactionsResponse {
  items: ShopGameWalletTransaction[];
  total: number;
}
